import { GQLProductFragment, GQLProductSideAvailableDecorationType, GQLStoreFragment } from '@theprintbar/graphql';
import { Merch, getTeamNameState, hasDecoration, isEMBDec, merchHasErrors, merchQuantity } from '@theprintbar/shared';

interface MerchStateProps {
	product: GQLProductFragment;
	merch?: Merch;
	updatingPrice: boolean;
	activeSide: string;
	store?: GQLStoreFragment | null;
	hasEmbWithoutStitches?: boolean;
	crew?: boolean;
	scrMinOrderQuantity?: number;
}

export function getMerchState({ product, merch, updatingPrice, activeSide, store, crew, scrMinOrderQuantity }: MerchStateProps) {
	let hasErrors = !!(merch && merchHasErrors(merch));
	const hasDecorations = !!merch?.decorations?.find(d => d.artwork?.size);

	const activeDecoration = merch?.decorations.find(d => d.side === activeSide);
	const quantity = merchQuantity(merch);
	const aboveMinOrder = !product.minOrder || (!!quantity && quantity >= product.minOrder);
	const teamNames = getTeamNameState(merch);
	const teamNamesGood = !teamNames.quantity || teamNames.quantity === teamNames.complete;
	const hasEmbWithoutStitches = merch?.decorations.some(d => isEMBDec(d) && !!d.artwork?.size && !d.meta.stitches);
	const canSameday =
		!!product.isSameday &&
		!!(
			!hasDecorations ||
			merch?.decorations.filter(d => !!d.artwork?.size).every(d => d.type === GQLProductSideAvailableDecorationType.Dtg)
		);
	const hasScr = merch?.decorations?.some(d => hasDecoration(d) && d.type === GQLProductSideAvailableDecorationType.Scr);
	const aboveMinScrOrder = !hasScr || (!!quantity && quantity >= (scrMinOrderQuantity || 25));

	let message = '';
	let title = '';

	if (!product.inStock) {
		hasErrors = true;
		(message = 'Product is out of stock'), (title = 'Out of stock');
	} else if (!hasDecorations) {
		(message = 'Add a design to get started'), (title = 'Add a Design');
	} else if (hasErrors) {
		(message = activeDecoration?.error?.message || 'Issues in your design'), (title = 'Issues in your design');
	} else if (!quantity && (!store || crew)) {
		(message = `Select quantity to order`), (title = 'Select Quantity');
	} else if (!aboveMinOrder && (!store || crew)) {
		hasErrors = true;
		(message = `Minimum order of ${product.minOrder} units is required`), (title = `Please add ${product.minOrder} units to proceed`);
	} else if (hasEmbWithoutStitches) {
		(message = 'Calculate stitches on all decorations'), (title = 'Calculate pricing');
	} else if (!teamNamesGood) {
		hasErrors = true;
		(message = 'Please enter a team name for each size'), (title = 'Complete Team Names');
	} else if (!aboveMinScrOrder) {
		hasErrors = true;
		(message = `Minimum order of ${scrMinOrderQuantity || 25} units is required for Screen Printing`),
			(title = `Screen printing requires minimum of ${scrMinOrderQuantity || 25} units`);
	} else {
		(message = crew || store ? 'Ready to add!' : 'Ready to order!'),
			(title = crew ? 'Add to order' : !!store ? 'Create Merch' : 'Add to cart');
	}
	const canSubmit = !hasErrors && !updatingPrice && hasDecorations && (!!quantity || (!!store && !crew)) && !hasEmbWithoutStitches;

	return {
		canSameday,
		hasErrors,
		hasDecorations,
		canSubmit,
		activeDecoration,
		hasEmbWithoutStitches,
		quantity,
		aboveMinOrder,
		message,
		title,
	};
}
